import React, { useState, useEffect } from "react";
import "./school.css";
import {
  wait,
  isEmpty,
  capitalizeAll,
  slugify,
  truncateValue,
} from "../../../../core/utils/helpers";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Button from "../../../../ui/atoms/buttons/Button";
import NotFound from "../../../../ui/organisms/not-found/404";
import StatusComponent from "../../../../ui/atoms/status/StatusComponent";
import Loading from "../../../../ui/atoms/loading/Loading";
//
import {
  onGetSchoolsWithPagination,
  onSchoolsReleased,
  onSchoolsArchived,
} from "../../services/school";
import { schoolSlice } from "../../../../app/store/slices/schoolSlice";
import Swal from "sweetalert2";
import useAxiosPrivate from "../../../../app/providers/context-provider/useAxiosPrivate";

const updated_at = moment(new Date()).format("YYYY-MM-DD HH:mm:ss");

const School = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  //
  const [isLoading, setIsLoading] = useState(true);
  const [schoolsData, setSchoolsData] = useState([]);
  const rowsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [isRefresh, setIsRefresh] = useState(false);
  //
  const [selectedRows, setSelectedRows] = useState([]);
  const [checkedRows, setCheckedRows] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      await wait(1500);

      let isMounted = true;
      const controller = new AbortController();
      const signal = controller.signal;

      const _localData = {
        rows_per_page: rowsPerPage,
        current_page: currentPage,
      };

      onGetSchoolsWithPagination(axiosPrivate, _localData, signal).then((response) => {
        const _data = response?.data;
        setSchoolsData(_data);
      });

      setIsLoading(false);

      return () => {
        isMounted = false;
        isMounted && controller.abort();
      };
    };

    fetchData();
  }, [currentPage, isRefresh]);

  const onHandleSelectRow = (id) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((item) => item !== id)
        : [...prevSelected, id],
    );
    setCheckedRows((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((item) => item !== id)
        : [...prevSelected, id],
    );
  };

  const onHandleSelectAll = (e) => {
    const isChecked = e.target.checked;
    if (isChecked) {
      const allSchoolsIds = schoolsData?.result?.map((item) => item.school_id);
      setSelectedRows(allSchoolsIds);
      setCheckedRows(allSchoolsIds);
    } else {
      setSelectedRows([]);
      setCheckedRows([]);
    }
  };
  const isAllSelected = selectedRows?.length === schoolsData?.result?.length;

  const onSchoolsReleasing = async (is_action) => {
    Swal.fire({
      title: `Do you really want to ${
        is_action === "released" ? "Release" : "UnRelease"
      } the selected ${selectedRows?.length > 1 ? "schools" : "school"} ?`,
      text: "This action impact the whole school(s), and you are able to revert the action!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${
        is_action === "released" ? "Release" : "UnRelease"
      }!`,
    }).then((result) => {
      if (result.isConfirmed) {
        // Perform delete action
        //
        Swal.fire({
          title: "Processing...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
        // Simulate async operation (e.g., API call)
        onSchoolsReleased(axiosPrivate,is_action, selectedRows, updated_at)
          .then((response) => {
            if (response?.data?.status) {
              Swal.close();
              //
              setIsRefresh((prevState) => !prevState);
              //
              Swal.fire(
                `${is_action === "released" ? "Release" : "UnReleased"}!`,
                `The selected school(s) has been ${
                  is_action === "released" ? "Released" : "UnReleased"
                }!`,
                "success",
              );
              setSelectedRows([]);
              setCheckedRows([]);
            }
          })
          .catch((error) => {
            Swal.close(); // close loading if error
            if (!error?.response) {
              Swal.fire("Error!", "No server response", "error");
            } else {
              Swal.fire(
                "Error!",
                error?.response?.data?.detail?.message,
                "error",
              );
            }
          });
      } else if (
        result.isDismissed ||
        result.dismiss === Swal.DismissReason.cancel
      ) {
        Swal.fire({
          title: "Cancelled",
          text: "The triggered action was cancelled!",
          icon: "error",
        });
      }
    });
  };

  const onSchoolsArchiving = async (is_action) => {
    Swal.fire({
      title: `Do you really want to ${
        is_action === "archived" ? "Archive" : "UnArchive"
      } the selected ${selectedRows?.length > 1 ? "schools" : "schools"} ?`,
      text: "This action impact the whole school(s), and you are able to revert the action!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${
        is_action === "archived" ? "Archive" : "UnArchive"
      }!`,
    }).then((result) => {
      if (result.isConfirmed) {
        // Perform delete action
        //
        Swal.fire({
          title: `${is_action === "archived" ? "Archiving" : "UnArchiving"}...`,
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
        // Simulate async operation (e.g., API call)
        onSchoolsArchived(axiosPrivate, is_action, selectedRows, updated_at)
          .then((response) => {
            if (response?.data?.status) {
              Swal.close();
              //
              setIsRefresh((prevSatte) => !prevSatte);
              //
              Swal.fire(
                `${is_action === "archived" ? "Archived" : "UnArchived"}!`,
                `The selected school(s) has been ${
                  is_action === "archived" ? "Archived" : "UnArchived"
                }!`,
                "success",
              );
              setSelectedRows([]);
              setCheckedRows([]);
            }
          })
          .catch((error) => {
            Swal.close(); // close loading if error
            if (!error?.response) {
              Swal.fire("Error!", "No server response", "error");
            } else {
              Swal.fire(
                "Error!",
                error?.response?.data?.detail?.message,
                "error",
              );
            }
          });
      } else if (
        result.isDismissed ||
        result.dismiss === Swal.DismissReason.cancel
      ) {
        Swal.fire({
          title: "Cancelled",
          text: "The triggered action was cancelled!",
          icon: "error",
        });
      }
    });
  };

  return (
    <div className="schools">
      {isLoading ? (
        <Loading type={"type-2"} />
      ) : (
        <React.Fragment>
          <div className="school-head">
            <h1>Schools Management</h1>
            <div className="w-full d-flex justify-space-between align-center">
              <Button
                type={"button"}
                preferedStyle={"default"}
                title={"➕ Create new school"}
                action={() => navigate("/admin/schools/create")}
              />
              {selectedRows.length > 0 && (
                <div className="sc-actions fade-in">
                  {selectedRows.length === 1 && (
                    <>
                      <Button
                        type={"button"}
                        preferedStyle={"default"}
                        title={"👁️ Voir details"}
                        action={() => {
                          // dispatch(
                          //   configurationSlice.actions.onCurrentTeamMember(
                          //     checkedTeamMember[0]
                          //   )
                          // );
                          navigate(
                            `/admin/users/details?userId=${selectedRows[0]}`,
                          );
                        }}
                      />
                      <Button
                        type={"button"}
                        preferedStyle={"default"}
                        title={"🔄 Update infos"}
                        action={() => navigate("/admin/users/update")}
                      />
                    </>
                  )}
                  <Button
                    type={"button"}
                    preferedStyle={"default"}
                    title={"🔐 Release"}
                    action={() => onSchoolsReleasing("released")}
                  />
                  <Button
                    type={"button"}
                    preferedStyle={"default"}
                    title={"🔐 UnRelease"}
                    action={() => onSchoolsReleasing("unreleased")}
                  />
                  <Button
                    type={"button"}
                    preferedStyle={"default"}
                    title={"📌 Archive"}
                    action={() => onSchoolsArchiving("archived")}
                  />
                  <Button
                    type={"button"}
                    preferedStyle={"default"}
                    title={"📌 UnArchive"}
                    action={() => onSchoolsArchiving("unarchived")}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="school-body">
            {isEmpty(schoolsData?.result) ? (
              <NotFound
                type={"data"}
                title={"No School found!"}
                text={"No School available yet."}
                isBoutton={false}
              />
            ) : (
              <React.Fragment>
                <div className="table">
                  <table>
                    <thead>
                      <tr>
                        <th className="col-05 text-align-center">
                          <input
                            type="checkbox"
                            checked={isAllSelected}
                            onChange={onHandleSelectAll}
                          />
                        </th>
                        <th className="col-20 text-align-left">
                          Date of creation
                        </th>
                        <th className="col-25 text-align-left">Name</th>
                        <th className="col-05 text-align-center">
                          Total Streams
                        </th>
                        <th className="col-05 text-align-center">
                          Total Inscriptions
                        </th>
                        <th className="col-10 text-align-center">
                          Total Female
                        </th>
                        <th className="col-10 text-align-center">Total Male</th>
                        <th className="col-05 text-align-center">Language</th>
                        <th className="col-05 text-align-center">
                          Status(Visibility)
                        </th>
                        <th className="col-05 text-align-center">
                          Status(Deletion)
                        </th>
                        <th className="col-05 text-align-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schoolsData?.result?.map((item, idx) => {
                        return (
                          <tr
                            key={idx}
                            className={
                              checkedRows.includes(item.school_id)
                                ? "row-checked"
                                : ""
                            }
                          >
                            <td className="col-05 text-align-center">
                              <input
                                type="checkbox"
                                checked={selectedRows.includes(item.school_id)}
                                onChange={() =>
                                  onHandleSelectRow(item.school_id)
                                }
                              />
                            </td>
                            <td className="col-15 text-align-left">
                              {moment(item?.school_created_at).format("LLLL")}
                            </td>
                            <td className="col-30 text-align-left">
                              <h3>{capitalizeAll(item?.school_title)}</h3>
                            </td>
                            <td className="col-05 text-align-center">
                              {item?.school_Streams}
                            </td>
                            <td className="col-05 text-align-center">
                              {item?.school_Inscriptions}
                            </td>
                            <td className="col-10 text-align-center">
                              {item?.total_female}
                            </td>
                            <td className="col-10 text-align-center">
                              {item?.total_male}
                            </td>
                            <td className="col-05 text-align-center">
                              <StatusComponent
                                type={
                                  item?.school_language === "en"
                                    ? "success"
                                    : "default"
                                }
                                title={
                                  item?.school_language === "en"
                                    ? "English"
                                    : "French"
                                }
                              />
                            </td>
                            <td className="col-05 text-align-center">
                              <StatusComponent
                                type={
                                  item?.school_is_activated
                                    ? "success"
                                    : "error"
                                }
                                title={
                                  item?.school_is_activated
                                    ? "Released"
                                    : "UnReleased"
                                }
                              />
                            </td>
                            <td className="col-05 text-align-center">
                              <StatusComponent
                                type={
                                  item?.school_is_deleted ? "error" : "success"
                                }
                                title={
                                  item?.school_is_deleted
                                    ? "Archived"
                                    : "UnArchived"
                                }
                              />
                            </td>
                            <td className="col-05 text-align-center">
                              <Button
                                type={"button"}
                                preferedStyle={"default"}
                                title={`more about ${truncateValue(
                                  capitalizeAll(item?.school_title),
                                  7,
                                )}`}
                                action={() => {
                                  dispatch(
                                    schoolSlice.actions.onCurrentSchool(item),
                                  );
                                  navigate(
                                    `/admin/schools/${slugify(
                                      item?.school_title,
                                    )}/${slugify(item?.school_id)}/streams`,
                                  );
                                }}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="pagination">
                  <div className="p-wrapper">
                    <button
                      className={
                        schoolsData?.result_complements?.current_page === 1
                          ? "button is-inactive"
                          : "button is-active"
                      }
                      onClick={() => {
                        setCurrentPage((prev) => (prev > 1 ? prev - 1 : 1));
                        setSelectedRows([]);
                      }}
                      disabled={
                        schoolsData?.result_complements?.current_page === 1
                      }
                    >
                      Previous
                    </button>
                    <span>
                      Page {currentPage} of{" "}
                      {schoolsData?.result_complements?.total_pages} pages.
                    </span>
                    <button
                      className={
                        schoolsData?.result_complements?.current_page ===
                        schoolsData?.result_complements?.total_pages
                          ? "button is-inactive"
                          : "button is-active"
                      }
                      onClick={() => {
                        setCurrentPage((prev) =>
                          prev <= schoolsData?.result_complements?.total_pages
                            ? prev + 1
                            : prev,
                        );
                        setSelectedRows([]);
                      }}
                      disabled={
                        schoolsData?.result_complements?.current_page ===
                        schoolsData?.result_complements?.total_pages
                      }
                    >
                      Next
                    </button>
                  </div>
                </div>
              </React.Fragment>
            )}
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

export default School;
